import React from "react";
import { X } from "lucide-react";
import { useCookiePreferences } from "../context/CookieContext";

function ToggleRow({ label, description, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-sm">
      <div className="pr-4">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        <div className="h-6 w-11 rounded-full bg-slate-200 transition peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-200 peer-disabled:cursor-not-allowed peer-disabled:bg-slate-200">
          <div className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5 peer-checked:bg-white peer-disabled:bg-slate-100" />
        </div>
      </label>
    </div>
  );
}

function CookiePreferencesModal() {
  const {
    preferences,
    isModalOpen,
    updatePreference,
    savePreferences,
    closePreferences,
  } = useCookiePreferences();

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[102] flex items-center justify-center bg-slate-900/60 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl border-2 border-slate-300">
        <div className="flex items-start justify-between border-b-2 border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Manage cookie preferences</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              Adjust how we use cookies. Essential cookies are always active for security and login.
            </p>
          </div>
          <button
            type="button"
            onClick={closePreferences}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close cookie preferences"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 px-6 py-5">
          <ToggleRow
            label="Essential cookies"
            description="Required for authentication, security, and load balancing. Always active."
            checked
            disabled
          />
          <ToggleRow
            label="Functional cookies"
            description="Improve experience (e.g., remembering settings). No ads or analytics."
            checked={preferences.functional}
            onChange={(value) => updatePreference("functional", value)}
          />
        </div>

        <div className="flex flex-col gap-2 border-t-2 border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={closePreferences}
            className="rounded-lg border-2 border-slate-400 px-5 py-2.5 text-sm font-bold text-slate-800 transition hover:border-slate-500 hover:bg-slate-50 active:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={savePreferences}
            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-blue-700 active:bg-blue-800"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookiePreferencesModal;

